/**
 * An extension of `Error` that requires that an error name be provided and can create a chain of error names
 * to illustrate the origin of an error.
 *
 * For example, if a network request throws NamedError("FetchTimeout", "The request timed out"), and then a
 * saga catches that error and throws its own NamedError("GetConfigurations", error), the resulting error name is
 * GetConfigurations/FetchTimeout with a message of "The request timed out".
 *
 * Adapted from Mercury's NamedError:
 * https://microsoft.visualstudio.com/DefaultCollection/Xbox.Apps/_git/Xbox.Apps.Mercury?path=%2Fshared%2Ftelemetry%2FNamedError.ts&_a=contents&version=GBmain
 */
export class NamedError extends Error {
  /**
   * An extension of `Error` that requires that an error name be provided.
   * @param name A useful error name.
   * @param error The base `Error`, or a message `string` otherwise.
   */
  public constructor(name: string, error: string | Error) {
    let wholeName = name;
    if (typeof error === "string") {
      super(error);
    } else {
      super(error.message);
      if ("name" in error && typeof error.name === "string" && error.name !== "Error") {
        wholeName = `${name}/${error.name}`;
      }
    }

    this.name = wholeName;
  }

  public toJSON() {
    return {
      name: this.name,
      message: this.message,
    };
  }
}
